import React, { useEffect, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, reduxForm } from 'redux-form';
import { Button, Form, FormGroup } from 'reactstrap';
import FormItem from '../../../common/forms/FormItem';
import Content from '../../../layout/Content';
import { ACTIVITY_BY_DRIVER_PATH } from '../../../../config/paths';
import { breadcrumbsPropTypes } from '../../../common/resource/proptypes/CommonPropTypes';
import {
  clearDriverActivity,
  getDriverActivity,
} from '../../../../actions/traffic/DriverActivity';
import Table from '../../../common/Table';
import Loader from '../../../common/Loader';
import Modal from '../../../common/modal/Modal';
import DriverMovementHistoryForm from './DriverMovementHistoryForm';
import {
  clearHolidayDates,
  getHolidayDates,
  postDriverMovementHistory,
} from '../../../../actions';
import { tzNormalizeDate } from '../../../../utils/date';
import { isRequired, validateEndDate } from '../../../../utils/validators';
import DriverSelect from '../../../common/forms/select/DriverSelect';
import FormFooter from '../../../common/forms/FormFooter';
import DatePicker from '../../../common/forms/input/DatePicker';

const validateDate = (value, allValues) =>
  validateEndDate(value, allValues.fromDateString);

const DriverActivity = ({
  breadcrumbs,
  driverActivity,
  loading,
  handleSubmit,
  dispatchClearDriverActivity,
  dispatchGetMonthlyDriverActivity,
  dispatchPostDriverMovementHistory,
  holidayDates,
  dispatchGetHolidayDates,
  dispatchClearHolidayDates,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [initialValues, setInitialValues] = useState(null);
  const [lastSearchParams, setLastSearchParams] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);

  useEffect(
    () => () => {
      dispatchClearDriverActivity();
      dispatchClearHolidayDates();
    },
    [],
  );

  const driver = useMemo(() => driverActivity.toJS(), [driverActivity]);

  const rows = useMemo(() => {
    if (!driver || !driver.days) return [];

    return Object.entries(driver.days).map(([date, activity]) => ({
      date,
      activity: activity || '',
    }));
  }, [driver]);

  const handleCreateActivity = useCallback(
    (dateKey) => {
      if (!driver) return;

      setInitialValues({
        driver: { value: driver.driverId, label: driver.fullName },
        startDate: tzNormalizeDate({
          date: dateKey,
          format: 'YYYY-MM-DDT00:00',
        }),
        endDate: tzNormalizeDate({ date: dateKey, format: 'YYYY-MM-DDT23:59' }),
      });
      setModalOpen(true);
    },
    [driver],
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setInitialValues(null);
  }, []);

  const refreshTable = useCallback(() => {
    if (lastSearchParams) {
      dispatchGetMonthlyDriverActivity(lastSearchParams);
    }
  }, [lastSearchParams, dispatchGetMonthlyDriverActivity]);

  const onSubmit = useCallback(
    (formValues) => {
      const newFormValues = {
        fromDateString: tzNormalizeDate({
          date: formValues.fromDateString,
          format: 'YYYY-MM-DD',
        }),
        toDateString: tzNormalizeDate({
          date: formValues.toDateString,
          format: 'YYYY-MM-DD',
        }),
        driverId: formValues.driverId.value,
      };

      setLastSearchParams(newFormValues);
      dispatchGetMonthlyDriverActivity(newFormValues);
      dispatchGetHolidayDates(newFormValues);
    },
    [dispatchGetMonthlyDriverActivity],
  );

  const onSubmitMovementHistoryForm = async (formValues) => {
    const newFormValues = {
      ...formValues,
      startDate: tzNormalizeDate({ date: formValues.startDate }),
      endDate: tzNormalizeDate({ date: formValues.endDate }),
      redirect: false,
      chargeMonth: formValues.month ? formValues.month.value : null,
      chargeYear: formValues.year || null,
    };

    setActivityLoading(true);
    await dispatchPostDriverMovementHistory(newFormValues);
    setActivityLoading(false);

    closeModal();
    refreshTable();
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Fecha',
        accessor: 'date',
        className: 'text-center',
        width: 140,
      },
      {
        Header: 'Actividades',
        accessor: 'activity',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value, original }) => {
          if (!value || value === '') {
            return (
              <Button
                color="link"
                size="sm"
                // eslint-disable-next-line react/prop-types
                onClick={() => handleCreateActivity(original.date)}
                title="Crear actividad"
              >
                <i className="fa fa-plus-circle text-muted" />
              </Button>
            );
          }
          // eslint-disable-next-line
          const lines = value.split('\n');
          return (
            <div style={{ whiteSpace: 'pre-line', fontSize: '0.85rem' }}>
              {lines.map((line, idx) => (
                // eslint-disable-next-line
                <div key={idx}>{line}</div>
              ))}
            </div>
          );
        },
        className: 'text-center',
      },
    ],
    [handleCreateActivity],
  );

  const holidaySet = useMemo(
    () => new Set(holidayDates ? holidayDates.toJS() : []),
    [holidayDates],
  );

  const content = (
    <>
      <Modal
        id="driver-movement-history-modal"
        title="Crear Actividad de Conductor"
        body={
          <DriverMovementHistoryForm
            onSubmit={onSubmitMovementHistoryForm}
            initialValues={initialValues}
            isEdit={false}
            disableSaveButton={activityLoading}
          />
        }
        show={modalOpen}
        onClickCancel={closeModal}
        onClickClose={closeModal}
      />

      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormGroup row>
          <FormItem label="Conductor" required>
            <Field
              name="driverId"
              component={DriverSelect}
              placeholder="Conductor"
              forHomeBase
              validate={[isRequired]}
            />
          </FormItem>
        </FormGroup>
        <FormGroup row>
          <FormItem label="Desde" required>
            <Field
              name="fromDateString"
              component={DatePicker}
              validate={[isRequired]}
            />
          </FormItem>
        </FormGroup>
        <FormGroup row>
          <FormItem label="Hasta" required>
            <Field
              name="toDateString"
              component={DatePicker}
              validate={[isRequired, validateDate]}
            />
          </FormItem>
        </FormGroup>
        <FormFooter
          saveButtonColor="primary"
          saveButtonText="Buscar"
          saveButtonIcon="fa fa-search"
          saveButtonDisabled={loading}
        />
      </Form>

      {driver && (
        <div className="border rounded p-3 my-3">
          <h5 className="mb-1">{driver.fullName}</h5>
          <div className="text-muted">
            DNI {driver.documentNumber} · {driver.driverType} ·{' '}
            {driver.baseLocation} · {driver.status}
            {driver.statusDescription &&
            driver.statusDescription !== driver.status
              ? ` (${driver.statusDescription})`
              : ''}
          </div>
        </div>
      )}

      {driver && (
        <Table
          columns={columns}
          data={rows}
          loading={loading}
          manual={false}
          defaultPageSize={rows.length > 0 ? rows.length : 31}
          generateStyle={(row) =>
            row && holidaySet.has(row.original.date)
              ? { background: '#7eedf5' }
              : {}
          }
        />
      )}

      {!driver && !loading && (
        <div className="alert alert-info mt-3">
          <i className="fa fa-info-circle" /> Seleccione un conductor y un rango
          de fechas para ver sus actividades
        </div>
      )}

      {loading && !driver && <Loader />}
    </>
  );

  return (
    <Content
      title="Tareo por Conductor"
      breadcrumbs={breadcrumbs}
      content={content}
    />
  );
};

DriverActivity.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  driverActivity: PropTypes.oneOfType([
    PropTypes.shape({
      driverId: PropTypes.number,
      fullName: PropTypes.string,
      days: PropTypes.objectOf(PropTypes.string),
      dayEventTypes: PropTypes.objectOf(PropTypes.string),
    }),
    PropTypes.arrayOf(PropTypes.object),
  ]),
  loading: PropTypes.bool,
  handleSubmit: PropTypes.func.isRequired,
  dispatchClearDriverActivity: PropTypes.func.isRequired,
  dispatchGetMonthlyDriverActivity: PropTypes.func.isRequired,
  dispatchPostDriverMovementHistory: PropTypes.func.isRequired,
  holidayDates: PropTypes.instanceOf(),
  dispatchGetHolidayDates: PropTypes.func.isRequired,
  dispatchClearHolidayDates: PropTypes.func.isRequired,
};

DriverActivity.defaultProps = {
  driverActivity: null,
  loading: false,
  holidayDates: null,
};

const mapStateToProps = ({ TrafficUnit, HumanResourcesUnit }) => ({
  breadcrumbs: [
    ...TrafficUnit.DriverUnitHome.get('breadcrumbs'),
    { text: 'Tareo por Conductor', href: ACTIVITY_BY_DRIVER_PATH },
  ],
  driverActivity: TrafficUnit.DriverActivity.getIn(['all', 'content']),
  holidayDates: HumanResourcesUnit.Holiday.getIn(['dates', 'content']),
  loading: TrafficUnit.DriverActivity.getIn(['all', 'loading']),
});

const mapDispatchToProps = {
  dispatchClearDriverActivity: clearDriverActivity,
  dispatchGetMonthlyDriverActivity: getDriverActivity,
  dispatchPostDriverMovementHistory: postDriverMovementHistory,
  dispatchGetHolidayDates: getHolidayDates,
  dispatchClearHolidayDates: clearHolidayDates,
};

const formComponent = reduxForm({ form: 'DriverActivityForm' })(DriverActivity);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
